import {
  useEditProfileSectionQuery,
  useSaveMyProfileMutation
} from 'generated/graphql';
import _ from 'lodash';
import {
  formUtil,
  useLoading,
  validationUtil
} from 'modules';
import * as yup from 'yup';



const __formSchema = yup
  .object({
    firstName: validationUtil.nonEmptyStringSchema.required('First name is required.'),
    lastName: validationUtil.nonEmptyStringSchema.required('Last name is required.'),
    websiteUrl: validationUtil.websiteUrlSchema,
    companyName: validationUtil.nonEmptyStringSchema.required('Company name is required.'),
    email: validationUtil.emailSchema.required('Email is required.'),
    phoneNumber: yup.string(),
    investmentCategories: validationUtil.investmentCategorySchema,
    investmentAreas: yup.array().of(yup.string().uuid().required()),
    investmentStages: yup.array().of(validationUtil.investmentStageSchema.required()),
    investmentGeographies: yup.array().of(validationUtil.investmentGeographySchema.required()),
    companyDescription: yup.string().max(250),
    checkSizeMinDollars: validationUtil.nonEmptyStringSchema,
    checkSizeMaxDollars: validationUtil.nonEmptyStringSchema,
    addressCity: validationUtil.nonEmptyStringSchema.required('City is required.'),
    addressUsState: yup.string(),
    addressCountry: validationUtil.nonEmptyStringSchema.required('Country is required.')
  })
  .required();


export type IFormState = yup.InferType<typeof __formSchema>;



export function useEditProfileSection() {
  const tuple = useEditProfileSectionQuery();
  const [saveMyProfile, saveMyProfileTuple] = __useSaveMyProfileMutation({
    userId: tuple.data?.loggedInUser?.id ?? '',
    companyProfileId: tuple.data?.loggedInUser?.companyProfile?.id ?? '',
    investorId: tuple.data?.loggedInUser?.investorProfile?.id ?? '',
    isInvestor: tuple.data?.loggedInUser?.type?.name === 'investor'
  });
  const loading = useLoading([tuple, saveMyProfileTuple]);

  const form = formUtil.useRemoteData2FormState_experimental({
    tuple: tuple as any,
    schema: __formSchema,
    fnGqlData2FormState: (gqlData: any) => {
      const loggedInUser = gqlData?.loggedInUser;
      const companyProfile = loggedInUser?.companyProfile;
      const investorProfile = loggedInUser?.investorProfile;
      return {
        firstName: loggedInUser?.firstName ?? '',
        lastName: loggedInUser?.lastName ?? '',
        companyName: companyProfile?.name ?? '',
        websiteUrl: companyProfile?.websiteUrl ?? '',
        email: loggedInUser?.email ?? '',
        phoneNumber: companyProfile?.phoneNumber ?? '',
        investmentCategories: companyProfile?.investmentCategory?.name,
        checkSizeMinDollars: investorProfile?.checkSizeMinDollars?.toString() ?? 'N/A',
        checkSizeMaxDollars: investorProfile?.checkSizeMaxDollars?.toString() ?? 'N/A',
        investmentAreas: _.map(companyProfile?.investmentAreas, area => area.id),
        investmentStages: _.map(companyProfile?.investmentStages, stage => stage.name),
        companyDescription: companyProfile?.description ?? '',
        investmentGeographies: _.map(companyProfile?.investmentGeographies, geography => geography.name),
        addressCity: companyProfile?.address?.city ?? '',
        addressUsState: companyProfile?.address.usState ?? '',
        addressCountry: companyProfile?.address?.country ?? ''
      };
    }
  });

  return {
    loading,
    form,
    data: {
      isInvestor: tuple.data?.loggedInUser?.type?.name === 'investor'
    },
    mutations: { saveMyProfile }
  };
}



function __useSaveMyProfileMutation({
  userId, companyProfileId, investorId, isInvestor
}:
{
  userId: string;
  companyProfileId: string;
  investorId: string;
  isInvestor: boolean;
}) {
  const [fnMutate, tuple] = useSaveMyProfileMutation();

  async function fnWrappedMutate(formData: IFormState) {
    await fnMutate({
      variables: {
        input: {
          id: userId,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName
        },
        profileInput: {
          id: companyProfileId,
          websiteUrl: formData.websiteUrl,
          name: formData.companyName,
          phoneNumber: formData.phoneNumber,
          investmentAreas: formData.investmentAreas,
          investmentGeographies: formData.investmentGeographies,
          investmentStages: formData.investmentStages,
          investmentCategory: formData.investmentCategories ?? null,
          description: formData.companyDescription,
          address: {
            city: formData.addressCity,
            country: formData.addressCountry,
            usState: formData.addressUsState
          }
        },
        investorProfile: {
          id: investorId,
          checkSizeMinDollars: formData.checkSizeMinDollars ? _.parseInt(formData.checkSizeMinDollars) : 0,
          checkSizeMaxDollars: formData.checkSizeMaxDollars ? _.parseInt(formData.checkSizeMaxDollars) : 0
        },
        isInvestor
      }
    });
  }

  return [
    fnWrappedMutate,
    {
      ...tuple,
      data: null
    }
  ] as const;
}
